import {
  FocusModeTemplateType,
  HabitPackType
} from 'interfaces/common.interface';
import { useCallback } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { getFocusTemplateById } from 'store/reducer/focus-mode/extra';
import {
  setCurrentViewedFocusTemplate,
  updateIsFocusTemplateFetching
} from 'store/reducer/focus-mode/slice';
import { getHabitPackById } from 'store/reducer/habit-pack/extra';
import {
  closePackPreview,
  openPackPreview,
  setCurrentViewedPack,
  updateIsCurrentHabitPackFetching
} from 'store/reducer/habit-pack/slice';
import { usePackAndFocusEditor } from 'components/pack-and-focus-editor';
import { PackAndFocusEntityType, PackEditorMode } from 'enums';
import { PickerVariant } from 'interfaces/marketplace.interface';

export const usePackPreviewDrawer = () => {
  const dispatch = useAppDispatch();
  const { openEditor } = usePackAndFocusEditor();
  const {
    isPackPreviewOpen,
    previewType,
    previewEntityId,
    previewVariant,
    isCurrentHabitPackFetching,
    isFocusTemplateFetching
  } = useAppSelectorShallow((state) => ({
    isPackPreviewOpen: state.habitPack.isPackPreviewOpen,
    previewType: state.habitPack.previewType,
    previewEntityId: state.habitPack.previewEntityId,
    previewVariant: state.habitPack.previewVariant,
    isCurrentHabitPackFetching: state.habitPack.isCurrentHabitPackFetching,
    isFocusTemplateFetching: state.focusMode.isFocusTemplateFetching
  }));

  const closePreview = useCallback(() => {
    dispatch(closePackPreview());
  }, [dispatch]);

  const openHabitPackPreview = useCallback(
    (pack: HabitPackType, variant: PickerVariant, fetchDetails = false) => {
      dispatch(setCurrentViewedPack(pack));
      dispatch(
        openPackPreview({
          previewType: PackAndFocusEntityType.HABIT_PACK,
          previewEntityId: pack.id,
          previewVariant: variant
        })
      );
      if (fetchDetails) {
        dispatch(updateIsCurrentHabitPackFetching(true));
        dispatch(getHabitPackById(pack.id));
      }
    },
    [dispatch]
  );

  const openFocusTemplatePreview = useCallback(
    (
      template: FocusModeTemplateType,
      variant: PickerVariant,
      fetchDetails = false
    ) => {
      if (!template.id) {
        return;
      }
      dispatch(setCurrentViewedFocusTemplate(template));
      dispatch(
        openPackPreview({
          previewType: PackAndFocusEntityType.FOCUS_TEMPLATE,
          previewEntityId: template.id,
          previewVariant: variant
        })
      );
      if (fetchDetails) {
        dispatch(updateIsFocusTemplateFetching(true));
        dispatch(getFocusTemplateById(template.id));
      }
    },
    [dispatch]
  );

  const openEditHabitPackFromPreview = useCallback(
    (pack: HabitPackType) => {
      dispatch(closePackPreview());
      openEditor({
        type: PackAndFocusEntityType.HABIT_PACK,
        mode: PackEditorMode.EDIT,
        pack
      });
    },
    [dispatch, openEditor]
  );

  const openEditFocusTemplateFromPreview = useCallback(
    (template: FocusModeTemplateType) => {
      dispatch(closePackPreview());
      openEditor({
        type: PackAndFocusEntityType.FOCUS_TEMPLATE,
        mode: PackEditorMode.EDIT,
        template
      });
    },
    [dispatch, openEditor]
  );

  const isPreviewLoading =
    previewType === PackAndFocusEntityType.HABIT_PACK
      ? isCurrentHabitPackFetching
      : isFocusTemplateFetching;

  return {
    isPackPreviewOpen,
    previewType,
    previewEntityId,
    previewVariant,
    isPreviewLoading,
    closePreview,
    openHabitPackPreview,
    openFocusTemplatePreview,
    openEditHabitPackFromPreview,
    openEditFocusTemplateFromPreview
  };
};
